/**
 * AnalysisHeader Component
 * Shows analysis freshness, document count, and refresh button.
 */

import React from 'react';
import { RefreshCw, Clock, AlertTriangle, FileText } from 'lucide-react';

interface AnalysisHeaderProps {
  analyzedAt: string | null;
  isStale: boolean;
  documentCount: number;
  isRefreshing: boolean;
  onRefresh: () => void;
}

function formatTimeAgo(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

export const AnalysisHeader: React.FC<AnalysisHeaderProps> = ({
  analyzedAt,
  isStale,
  documentCount,
  isRefreshing,
  onRefresh,
}) => {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Project Insights</h1>
        <div className="mt-1 flex flex-wrap items-center gap-3 text-sm">
          {/* Document count */}
          <span className="flex items-center gap-1.5 text-slate-600">
            <FileText className="w-4 h-4" />
            {documentCount} document{documentCount !== 1 ? 's' : ''}
          </span>

          {/* Last analyzed */}
          {analyzedAt && (
            <span className="flex items-center gap-1.5 text-slate-500">
              <Clock className="w-4 h-4" />
              Analyzed {formatTimeAgo(analyzedAt)}
            </span>
          )}

          {/* Stale warning */}
          {isStale && (
            <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">
              <AlertTriangle className="w-3.5 h-3.5" />
              Outdated
            </span>
          )}
        </div>
      </div>

      {/* Refresh button */}
      <button
        onClick={onRefresh}
        disabled={isRefreshing}
        className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
          isRefreshing
            ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
            : 'bg-indigo-600 text-white hover:bg-indigo-700'
        }`}
      >
        <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
        {isRefreshing ? 'Analyzing...' : isStale ? 'Refresh Analysis' : 'Re-analyze'}
      </button>
    </div>
  );
};

export default AnalysisHeader;
